import { useEffect } from "react";
import { branding } from "@creators/backend/lib/config";

function appOrigin(): string {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return `https://${branding.domain}`;
}

export type SeoProps = {
  title: string;
  description: string;
  /** Path only, e.g. `/` or `/aanya`. */
  path: string;
  /** default | noindex for authenticated/app surfaces */
  robots?: "index,follow" | "noindex,nofollow";
  /** Absolute or site-relative image URL for Open Graph. */
  image?: string;
  type?: "website" | "profile";
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
};

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJsonLd(data: Record<string, unknown> | Record<string, unknown>[]) {
  const id = "seo-json-ld";
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data).replace(/</g, "\\u003c");
}

/**
 * Absolute site URL for a path.
 *
 * HTML pages end with `/` so the canonical matches what S3/CloudFront actually
 * serves (folders 302 from `/features` → `/features/`). Asset paths keep their
 * extension and no trailing slash.
 */
export function siteUrl(path = "/"): string {
  const base = appOrigin().replace(/\/+$/, "");
  if (!path || path === "/") return `${base}/`;
  const normalized = path.startsWith("/") ? path : `/${path}`;
  if (/\.[a-zA-Z0-9]+$/.test(normalized)) return `${base}${normalized}`;
  return `${base}${normalized.replace(/\/+$/, "")}/`;
}

/**
 * Sets document title + description, canonical, Open Graph / Twitter tags.
 * Call once per page; cleans up JSON-LD on unmount when provided.
 */
export function Seo({
  title,
  description,
  path,
  robots = "index,follow",
  image = DEFAULT_OG_IMAGE,
  type = "website",
  jsonLd,
}: SeoProps) {
  useEffect(() => {
    const url = siteUrl(path);
    const ogImage = image.startsWith("http") ? image : siteUrl(image);

    document.title = title;
    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", robots);
    upsertLink("canonical", url);

    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:site_name", branding.name);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", ogImage);
    upsertMeta("property", "og:image:width", "1200");
    upsertMeta("property", "og:image:height", "630");

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", ogImage);

    if (jsonLd) upsertJsonLd(jsonLd);

    return () => {
      const el = document.getElementById("seo-json-ld");
      if (el) el.remove();
    };
  }, [title, description, path, robots, image, type, JSON.stringify(jsonLd ?? null)]);

  return null;
}

export const HOME_DESCRIPTION =
  "One link for inbound collab offers. Counter until the terms fit, freeze them into a contract, and get paid straight to your UPI or bank — with a clock on every step so nobody has to chase.";

/** Default Open Graph / Twitter large-card image (1200×630). */
export const DEFAULT_OG_IMAGE = "/og-banner.png";
